<template>
	<p>{{ text }}{{ language === 'ja' ? '。' : '. ' }}</p>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { useSpinningText } from '@/helpers/composables/useSpinningText';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

const { language } = storeToRefs(useLanguageStore());

const props = defineProps<{
	title: string;
	offers: TitleDetail['offers'];
	seasonNumber?: number | undefined;
}>();

const { getOfferTextBlock, getInfoTextBlock, getRTLInfoTextBlock } = useSpinningText(toRefs(props));

const text = computed(() => {
	switch (language.value) {
		case 'fi':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Tällä hetkellä voit katsoa elokuvan',
				'ilmaiseksi mainosten kanssa suoratoistona palvelussa'
			);
		case 'ru':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'В настоящее время Вы можете посмотреть',
				'на',
				'бесплатно с рекламой'
			);
		case 'it':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Adesso puoi guardare',
				'in streaming su',
				'gratuitamente con avvisi pubblicitari'
			);
		case 'de':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Du kannst',
				'legal bei',
				'kostenlos mit Werbeunterbrechungen im Stream anschauen'
			);
		case 'fr':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'En ce moment, vous pouvez regarder',
				'en streaming gratuit avec publicités sur'
			);
		case 'es':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Actualmente, usted es capaz de ver',
				'forma gratuita con anuncios'
			);
		case 'ja':
			return getInfoTextBlock(MonetizationType.Ads, 'は広告付きの動画が', 'で無料配信中です');
		case 'ko':
			return getOfferTextBlock(
				MonetizationType.Ads,
				'현재 ',
				`에서 광고가 포함된 "${props.title}"의 영상을 무료 배포 중입니다`
			);
		case 'ro':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'În prezent, puteți viziona',
				'streaming pe',
				'gratuit cu afișare de anunțuri'
			);
		case 'hu':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Jelenleg a(z)',
				'adatfolyamként elérhető a(z)',
				'szolgáltatónál ingyenesen reklámokkal.'
			);
		case 'cs':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Aktuálně máte možnost sledovat',
				'streaming u',
				'zdarma s reklamami'
			);
		case 'bg':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'В момента можете да гледате стрийминг на',
				'в',
				'безплатно с реклами в'
			);
		case 'ar':
			return getInfoTextBlock(MonetizationType.Ads, 'يمكنكم حالياً مشاهدة', 'على', 'مجاناً مع فواصل إعلانية');
		case 'pt':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Atualmente você pode assistir',
				'em streaming via',
				'gratuitamente com anúncios'
			);
		case 'tr':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Şu an',
				'adlı yapımı',
				'platformunda ücretsiz ama reklamlı olarak izleyebilirsiniz'
			);
		case 'sq':
			return getInfoTextBlock(MonetizationType.Ads, 'Aktualisht, ju mund të shikoni', 'në', 'falas me reklama');
		case 'pl':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Obecnie możesz oglądać',
				'w streamingu w serwisie',
				'za darmo z reklamami'
			);
		case 'az':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Hazırda siz',
				'üzərindən',
				'yayımını reklamlarla ödənişsiz izləyə bilərsiniz'
			);
		case 'el':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Προς το παρόν μπορείτε να παρακολουθήσετε τον τίτλο',
				'με streaming στο',
				'δωρεάν με διαφημίσεις'
			);
		case 'sr':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Trenutno možete da gledate',
				'strim na',
				'besplatno uz oglase'
			);
		case 'sv':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'För närvarande kan du se',
				'streama på',
				'gratis med annonser'
			);
		case 'uk':
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Наразі ви можете дивитися трансляцію',
				'на',
				'безкоштовно з рекламою'
			);
		case 'ur':
			return getRTLInfoTextBlock(
				MonetizationType.Ads,
				'فی الحال آپ اشتہارات کے ساتھ مفت میں ',
				'پر',
				'اسٹریمنگ دیکھنے کے قابل ہیں۔'
			);
		case 'zh':
			// Not RTL, but using a similar structure
			return getRTLInfoTextBlock(MonetizationType.Ads, '目前你可以在', '免费带广告观看', '。');
		case 'en':
		default:
			return getInfoTextBlock(
				MonetizationType.Ads,
				'Currently you are able to watch',
				'streaming on',
				'for free with ads'
			);
	}
});
</script>
